import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  ChevronDown,
  Download,
  Plus,
  WalletCards,
} from "lucide-react";
import api from "../lib/api";
import { money, monthLabel } from "../lib/format";
import { notify } from "../lib/toast";
import { checkBudgetAfterTx } from "../lib/budget";
import MetricCard from "../components/MetricCard";
import TransactionRow from "../components/TransactionRow";
import TransactionModal from "../components/TransactionModal";

const currentMonthLabel = () =>
  new Date().toLocaleDateString("tr-TR", { month: "long", year: "numeric" }).toUpperCase();

function EmptyState({ title, description, action }) {
  return (
    <div className="empty-inline" data-testid="empty-state">
      <strong>{title}</strong>
      <span>{description}</span>
      {action}
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);

  const load = async () => {
    try {
      const [d, a, c] = await Promise.all([
        api.get("/dashboard"),
        api.get("/accounts"),
        api.get("/contacts"),
      ]);
      setData(d.data);
      setAccounts(a.data);
      setContacts(c.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Veriler yüklenemedi");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (item) => {
    await api.post("/transactions", item);
    notify.success("İşlem eklendi");
    checkBudgetAfterTx(item);
    await load();
  };

  const series = data?.series || [];
  const maxSeries = useMemo(() => {
    if (!series.length) return 1;
    return Math.max(...series.map((s) => Math.max(s.income, s.expense, s.net))) || 1;
  }, [series]);

  const yAxis = useMemo(() => {
    const step = maxSeries / 4;
    return [4, 3, 2, 1, 0].map((i) => `₺${Math.round((step * i) / 1000)}K`);
  }, [maxSeries]);

  const pathFor = (arr) => {
    if (!arr.length) return "";
    const w = 700 / Math.max(arr.length - 1, 1);
    return arr
      .map((v, i) => {
        const x = i * w;
        const y = 200 - (v / maxSeries) * 180;
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
  };

  const areaPath = (arr) => {
    const p = pathFor(arr);
    return `${p} L700,220 L0,220Z`;
  };

  if (error) {
    return (
      <div className="page-error" data-testid="dashboard-error">
        <h3>Bir sorun oluştu</h3>
        <p>{error}</p>
        <button className="primary-btn" onClick={load} data-testid="retry-dashboard">
          Tekrar dene
        </button>
      </div>
    );
  }

  if (!data) {
    return <div className="page-loading" data-testid="dashboard-loading">Yükleniyor…</div>;
  }

  const { metrics, transactions, upcoming } = data;
  const netSeries = series.map((s) => s.net);
  const incSeries = series.map((s) => s.income);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">{currentMonthLabel()}</div>
          <h1>Finansal nabzınız burada.</h1>
          <p>Şirketinizin finansal görünümü ve nakit hareketleri.</p>
        </div>
        <div className="head-actions">
          <Link to="/reports" className="secondary-btn" data-testid="export-link">
            <Download size={16} /> Rapor al
          </Link>
          <button className="primary-btn" onClick={() => setModal(true)} data-testid="add-transaction-button">
            <Plus size={17} /> İşlem ekle
          </button>
        </div>
      </div>

      <section className="metric-grid">
        <MetricCard
          label="Toplam nakit"
          value={money(metrics.cash)}
          hint="Tüm hesaplar"
          tone="blue"
          Icon={WalletCards}
          testid="metric-cash"
        />
        <MetricCard
          label="Aylık gelir"
          value={money(metrics.income)}
          hint="Bu ay"
          tone="green"
          Icon={ArrowDownRight}
          testid="metric-income"
        />
        <MetricCard
          label="Aylık gider"
          value={money(metrics.expense)}
          hint="Bu ay"
          tone="amber"
          Icon={ArrowUpRight}
          testid="metric-expense"
        />
        <MetricCard
          label="Net nakit akışı"
          value={money(metrics.net)}
          hint="Bu ay"
          tone="blue"
          Icon={BarChart3}
          testid="metric-net"
        />
      </section>

      <section className="dashboard-grid">
        <div className="panel cash-panel">
          <div className="panel-head">
            <div>
              <h2>Nakit akışı</h2>
              <span>Son 6 ay · Tüm hesaplar</span>
            </div>
            <button className="select-btn" data-testid="cashflow-period-select">
              Son 6 ay <ChevronDown size={14} />
            </button>
          </div>
          <div className="chart">
            <div className="chart-y">
              {yAxis.map((v, i) => (
                <span key={`y${i}`}>{v}</span>
              ))}
            </div>
            <div className="chart-area">
              <div className="grid-lines" />
              <svg viewBox="0 0 700 220" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="fill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor="#3b82f6" stopOpacity=".28" />
                    <stop offset="1" stopColor="#3b82f6" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={areaPath(incSeries)} fill="url(#fill)" />
                <path d={pathFor(incSeries)} fill="none" stroke="#5b9bff" strokeWidth="3" />
                <path d={pathFor(netSeries)} fill="none" stroke="#10b981" strokeWidth="2" strokeDasharray="4 4" />
              </svg>
              <div className="chart-labels">
                {series.map((s) => (
                  <span key={s.key}>{monthLabel(s.key)}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="chart-legend">
            <span><i style={{ background: "#5b9bff" }} /> Gelir</span>
            <span><i style={{ background: "#10b981" }} /> Net akış</span>
          </div>
        </div>

        <div className="panel upcoming-panel">
          <div className="panel-head">
            <div>
              <h2>Yaklaşan hareketler</h2>
              <span>Planlanan ödeme ve tahsilatlar</span>
            </div>
            <Link to="/upcoming" className="more-btn" data-testid="upcoming-more-button">
              Tümünü gör <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="upcoming-list">
            {upcoming.length === 0 ? (
              <EmptyState
                title="Henüz planlı hareket yok"
                description="Yaklaşan tahsilat ve ödemelerinizi ekleyin."
                action={
                  <Link to="/upcoming" className="secondary-btn" data-testid="add-upcoming-link">
                    <Plus size={14} /> Yeni ekle
                  </Link>
                }
              />
            ) : (
              upcoming.map((u) => (
                <div className="upcoming" key={u.id} data-testid={`upcoming-${u.id}`}>
                  <div className={`date-box ${u.tone}`}>
                    <strong>{(u.date || "").slice(8, 10)}</strong>
                    <small>{monthLabel((u.date || "").slice(0, 7))}</small>
                  </div>
                  <div className="upcoming-title">
                    <strong>{u.title}</strong>
                    <span>{u.kind}</span>
                  </div>
                  <div className={u.tone === "income" ? "income-text" : "expense-text"}>
                    {u.tone === "income" ? "+" : "−"}
                    {money(u.amount)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="panel transactions-panel">
        <div className="panel-head">
          <div>
            <h2>Son işlemler</h2>
            <span>En güncel finans hareketleri</span>
          </div>
          <Link to="/transactions" className="more-btn" data-testid="view-all-transactions">
            Tüm işlemler <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="transaction-list">
          {transactions.length === 0 ? (
            <EmptyState
              title="Henüz işlem yok"
              description="İlk gelir veya giderinizi eklemek için başlayın."
              action={
                <button className="primary-btn" onClick={() => setModal(true)} data-testid="empty-add-transaction">
                  <Plus size={14} /> İşlem ekle
                </button>
              }
            />
          ) : (
            transactions.map((t) => <TransactionRow item={t} key={t.id} />)
          )}
        </div>
      </section>

      <TransactionModal
        open={modal}
        onClose={() => setModal(false)}
        onSave={save}
        accounts={accounts}
        contacts={contacts}
      />
    </>
  );
}
